/**
 * 可序列化 plain data。
 */
export type SerializablePlainData =
    | string
    | number
    | boolean
    | null
    | readonly SerializablePlainData[]
    | {readonly [key: string]: SerializablePlainData};

/**
 * 判断对象是否为 plain object。
 *
 * @param value 待判断值。
 * @returns 是否为 plain object。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 判断值是否为可序列化 plain data。
 *
 * 设计说明：
 * 1. history snapshot 只能保存 CAD 主状态；
 * 2. 禁止函数、class 实例、Date、Map、Set、Three.js 对象、BRep/Mesh 等运行时对象进入历史；
 * 3. 该函数只判断数据形态，具体字段语义由各领域 adapter 负责。
 *
 * @param value 待判断值。
 * @returns 是否为可序列化 plain data。
 */
export function isSerializablePlainData(value: unknown): value is SerializablePlainData {
    if (value === null) {
        return true;
    }

    const valueType = typeof value;

    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
        return Number.isFinite(value as number) || valueType !== 'number';
    }

    if (Array.isArray(value)) {
        return value.every(isSerializablePlainData);
    }

    if (valueType !== 'object') {
        return false;
    }

    if (!isPlainObject(value as object)) {
        return false;
    }

    return Object.values(value as Record<string, unknown>).every(isSerializablePlainData);
}

/**
 * 判断值是否为可序列化 plain record。
 *
 * @param value 待判断值。
 * @returns 是否为可序列化 plain record。
 */
export function isSerializablePlainRecord(value: unknown): value is Readonly<Record<string, SerializablePlainData>> {
    return typeof value === 'object'
        && value !== null
        && !Array.isArray(value)
        && isPlainObject(value)
        && Object.values(value as Record<string, unknown>).every(isSerializablePlainData);
}

